import { isEmpty } from "lodash-es";

import { EntityType } from "@mapmycustomers/shared/enum";
import Company from "@mapmycustomers/shared/types/entity/Company";
import Person from "@mapmycustomers/shared/types/entity/Person";
import { RoutePreview, RoutePreviewMetrics } from "@mapmycustomers/shared/types/entity/Route";
import { RouteLegMetric } from "@mapmycustomers/shared/types/entity/RouteDetail";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import getRoutePreviewMetrics from "@app/util/route/routePreviewMetrics";
import displayOrderComparator from "@app/util/sorting/displayOrderComparator";

import { EditorRouteStop } from "../store/routeDetails/types/RouteStop";
import correlateManualStopsToPreview from "../store/routeDetails/utils/correlateManualStopsToPreview";

const toLegMetric = (source: RouteLegMetric | undefined): RouteLegMetric => ({
  ...(source?.travelDistance == null ? {} : { travelDistance: source.travelDistance }),
  ...(source?.travelTime == null ? {} : { travelTime: source.travelTime }),
});

/**
 * The ordered leg array the bulk-stops envelope persists: one entry per stop, plus a trailing
 * entry for the drive to the end location.
 *
 * Length is exact, not a range. The BE's read gate is
 * `hasEndLocation ? legs === stops + 1 : legs === stops` — an XOR, so the trailing entry is
 * required with an end location and rejected without one. (The write gate accepts either shape,
 * because that path does not load `routeDetail`; an array that satisfies only the write gate
 * persists and is then silently discarded on every read.)
 *
 * `routePreview.endLocation` is the signal for which shape to send, and it is exact: every solve
 * branch emits it if and only if the request carried an `endGeoPoint`, so its presence is also
 * the presence of a final leg to report. A route cannot be saved without a fresh preview, so the
 * preview always describes the route being written.
 *
 * The BE re-projects the array onto `stopTime[]` positionally, so it is indexed by
 * `displayOrder`, not by the preview's order, which a re-optimization can differ from. The sort
 * below is load-bearing rather than defensive: the bulk-stops read orders `displayOrder, id`,
 * but the unified-route read orders by `displayOrder` alone, so rows sharing a `displayOrder`
 * have no agreed tiebreak and a mismatch would hand each stop its neighbour's ETA. Stops the
 * preview does not cover (checked-off ones are excluded from every preview) still occupy their
 * slot as an empty object: the projection skips a leg with no keys, so the array keeps a length
 * the BE will accept instead of being dropped wholesale.
 *
 * Preview stops pair to route stops the way the read side pairs them — by entity id, and
 * positionally for manual stops — so the two cannot drift apart. Keying by entity id relies on
 * one entity being at most one stop per route, which the product enforces end to end: every add
 * path dedupes on `${entityType}:${entityId}`, a transfer skips an entity already on the
 * destination (w0003), and the read selector `getRouteStops` builds the same id map.
 */
const buildRouteLegMetrics = (
  stops: ReadonlyArray<EditorRouteStop>,
  routePreview: RoutePreview
): RouteLegMetric[] | undefined => {
  const previewByCompanyId = new Map<Company["id"], RouteLegMetric>();
  const previewByPersonId = new Map<Person["id"], RouteLegMetric>();
  const previewStops = routePreview.stopTime ?? [];
  previewStops.forEach((previewStop) => {
    if (previewStop.entity === EntityType.COMPANY) {
      previewByCompanyId.set(previewStop.accountId, previewStop);
    } else if (previewStop.entity === EntityType.PERSON) {
      previewByPersonId.set(previewStop.contactId, previewStop);
    }
  });
  const routeStops = (stops ?? []).toSorted(displayOrderComparator);
  const manualMatches = correlateManualStopsToPreview(
    routeStops.filter((stop) => !stop.checkedOff),
    previewStops
  );

  const legs = routeStops.map((stop) =>
    toLegMetric(
      stop.entity === EntityType.COMPANY
        ? previewByCompanyId.get(stop.accountId)
        : stop.entity === EntityType.PERSON
          ? previewByPersonId.get(stop.contactId)
          : isDefined(stop.id)
            ? manualMatches.get(stop.id)
            : undefined
    )
  );

  const allLegs = routePreview.endLocation
    ? [...legs, toLegMetric(routePreview.endLocation)]
    : legs;
  // Nothing matched, so the array would only occupy the column with slots the read path skips.
  return allLegs.some((leg) => !isEmpty(leg)) ? allLegs : undefined;
};

// The full metrics envelope. Only create and bulk-stops can carry the leg array; the route-level
// PUT sends getRoutePreviewMetrics on its own.
export const buildRoutePreviewMetrics = (
  stops: ReadonlyArray<EditorRouteStop>,
  routePreview: RoutePreview
): RoutePreviewMetrics => {
  const legMetrics = buildRouteLegMetrics(stops, routePreview);
  return {
    ...getRoutePreviewMetrics(routePreview),
    ...(legMetrics ? { legMetrics } : {}),
  };
};

export default buildRouteLegMetrics;
